<template>
    <div class="data-table">

        <!-- filters (optional) -->
        <b-row v-if="showFilters">
            <b-col md="12" class="my-1">
                <b-card class="text-left mb-3">
                    <h3 slot="header"
                        class="my-0">
                        {{ $i18n.gt('HEADING_SEARCH') }}
                        <b-btn variant="primary"
                               size="sm"
                               class="float-right my-0"
                               @click="resetFilters()">
                            {{ $i18n.gt('LABEL_RESET') }}
                        </b-btn>
                        <slot name="headerExtra"/>
                    </h3>

                    <!-- slot for optional custom rendering of filters -->
                    <slot name="filters">

                        <!-- default content: generic filter form based on filters spec -->
                        <b-row>
                            <b-col v-for="(f,i) in nonHiddenFilters" :key="i">
                                <b-form-group :label="f.label">

                                    <!-- string input -->
                                    <b-form-input v-if="f.type == 'string'"
                                                  type="text"
                                                  v-model="filters[f.field].value"
                                                  @input="filterChanged()">
                                    </b-form-input>

                                    <!-- date input -->
                                    <b-form-input v-if="f.type == 'date'"
                                                  type="date"
                                                  v-model="filters[f.field].value"
                                                  @input="filterChanged()">
                                    </b-form-input>

                                    <!-- select -->
                                    <b-form-select v-if="f.type == 'select'"
                                                   v-model="filters[f.field].value"
                                                   :options="f.options"
                                                   @change="filterChanged()">
                                    </b-form-select>

                                </b-form-group>
                            </b-col>
                        </b-row>

                        <!-- ... or the parent component can define a custom template, using arbitrary inputs
                            based on the defined filters:

                            <b-form-group horizontal label="Email">
                                <b-form-input v-model="filters.email.value"/>
                            </b-form-group>
                        -->
                    </slot>
                </b-card>
            </b-col>
        </b-row>

        <!-- pager (optional) -->
        <b-row v-if="showPager" class="data-table__pager">
            <b-col md="4" class="my-1">
                <b-pagination :total-rows="context.totalRows"
                              :per-page="context.perPage"
                              v-model="context.currentPage" class="my-0"/>
            </b-col>
            <b-col md="5" class="my-1">
                <div class="show-per-page">
                    {{ $i18n.gt('LABEL_SHOW') }}
                    <b-form-select class="per-page-select"
                                   v-model="context.perPage">
                        <option value="10">10</option>
                        <option value="20">20</option>
                        <option value="50">50</option>
                        <option value="100">100</option>
                        <option value="500">500</option>
                    </b-form-select>
                    {{ $i18n.gt('LABEL_ITEMS_PER_PAGE') }}
                </div>
            </b-col>
            <b-col md="3" class="my-1 text-right">
                {{ context.totalRows }} {{ $i18n.gt('LABEL_ITEMS') }}
            </b-col>

        </b-row>

        <b-table striped
                 :items="itemProvider"
                 ref="table"
                 :id="tableId"
                 :fields="allFields"
                 :current-page="context.currentPage"
                 :per-page="context.perPage"
                 :sort-by.sync="context.sortBy"
                 :sort-desc.sync="context.sortDesc"
                 no-local-sorting
                 :busy.sync="isBusy">

            <!-- loading spinner -->
            <template v-slot:table-busy>
                <div class="text-center text-info my-2">
                    <b-spinner class="align-middle"></b-spinner>
                    <strong class="px-3">{{ $i18n.gt('LABEL_LOADING') }}</strong>
                </div>
            </template>

            <!-- template for show-details column -->
            <template v-slot:cell(show_details)="row">
                <!-- we use @click.stop here to prevent emitting of a 'row-clicked' event  -->
                <b-button size="sm" class="btn-icon"
                          @click.stop="row.toggleDetails">
                    <shs-icon :name="row.detailsShowing ? 'chevron-bottom' : 'chevron-right'"></shs-icon>
                </b-button>
            </template>

            <!-- template for custom row details display -->
            <template v-slot:row-details="row">
                <slot name="customRowDetails" v-bind:row="row">
                    <!-- the parent component can define a custom template for this slot - e.g.:

                        <template v-slot:customRowDetails="slotProps">
                            My row ID is: {{ slotProps.row.item.id }}
                        </template>
                    -->
                </slot>
            </template>

            <!-- template for custom column rendering -->
            <template v-slot:cell()="row">
                <slot name="customField" v-bind:row="row">
                    <!-- default content: -->

                    <!-- if the field has a 'get' method (computed value) -->
                    <div v-if="row.field.get">
                        {{row.field.get(row.item)}}
                    </div>
                    <!-- otherwise -->
                    <div v-else>
                        {{row.item[row.field.key]}}
                    </div>

                    <!-- ... or the parent component can define custom templates for arbitrary fields, e.g.:

                        <template v-slot:customField="slotProps">
                            <div v-if="slotProps.row.field.key == 'field1'">
                                My field1 value: {{ slotProps.row.item.field1 }}
                            </div>
                        </template>

                        If none of the custom templates match for a given column, the above default rendering
                        rules will be used.
                    -->
                </slot>
            </template>

        </b-table>

    </div>
</template>

<!-- ----------------------------------------------------------------------- -->

<script>
    /*
     * This component wraps b-table and b-pagination to provide a generic data table with sortable columns,
     * plus optional filters and pagination.
     *
     * See comments in template above for custom column rendering.
     *
     * Table columns and filters are specified via 'fields' and 'filters', resp., in the data method
     * (OR as computed properties).
     *
     * data() {
     *     return {
     *         ...,
     *         fields: [
     *             { key: 'myField', sortable: true, label: 'My Field' },
     *             { key: 'participant.firstname', sortable: true, label: 'First Name',
     *               get: (item) => { return item.participant.firstname } },
     *         ],
     *         filters: {
     *             text: {field: 'text', type: 'string', value: null, label: 'First/Last Name'},
     *             type: {field: 'type', type: 'select', value: null, label: 'Type', options: this.typeOptions},
     *             practiceId: {field: 'practiceId', type: 'hidden', value: this.practiceId},
     *         }
     *
     * Allowable filter types are 'string', 'select', or 'hidden'.
     * For select filters, must also specify 'options' in format [{value: X, text: Y}, ...].
     *
     * Field specifications can also include a 'get' function, as in the participant.firstname example,
     * in order to calculate a display value from the row's data item.
     */

    import Vue from 'vue'
    import VueResource from 'vue-resource'
    import * as _ from 'lodash'

    Vue.use(VueResource)

    export default Vue.extend({
        name: 'dataTable',
        props: {
            tableId: { type: String, default: 'table' },
            showFilters: Boolean,
            showPager: Boolean,
            showDetails: Boolean,
            getItems: Function,
            filters: Object,
            fields: Array,
        },
        data () {
            return {
                context: {
                    totalRows: null,
                    perPage: 50,
                    currentPage: 1,
                    sortBy: 'id',
                    sortDesc: false,
                },
                isBusy: false,
            }
        },
        computed: {
            allFields () {
                const detailsFields = (this.showDetails
                    ? [{ key: 'show_details', sortable: false, label: '' }]
                    : [])
                return _.concat(detailsFields, this.fields)
            },
            nonHiddenFilters () {
                return _.filter(this.filters, (f) => {
                    return f.type != 'hidden'
                })
            },
            hasItems () {
                return (this.context.totalRows > 0);
            }
        },
        methods: {
            getCustomColumns () {
                return _.map(
                    _.filter(this.fields, (f) => f.custom),
                    (cf) => cf.key
                )
            },
            itemProvider (ctx) {
                return this.getItems(ctx).then((items) => {
                    this.context = ctx
                    if (items && items.length == 1) {
                        Vue.set(items[0], '_showDetails', true)
                    }
                    return items
                })
            },
            resetFilters () {
                _.each(this.filters, (f) => {
                    if (f.type != 'hidden') {
                        f.value = null
                    }
                })
                this.refresh() // do it now (this will result in two refreshes with the watch below)
            },
            filterChanged () {
                this.debouncedRefresh()
            },

            refresh () {
                this.$refs.table.refresh()
            }
        },
        created () {
            this.debouncedRefresh = _.debounce(this.refresh, 500)
        },
        watch: {
            filters: {
                deep: true,
                handler: function (newVal, oldVal) {
                    if (!this.$refs.table.busy) {
                        this.debouncedRefresh()
                    }
                },
            }

        }
    })
</script>

<!-- ----------------------------------------------------------------------- -->

<style lang="scss">
    .data-table {
        .show-per-page {
            display: inline;

            select {
                display: inline;
                width: 75px;
            }
        }

        .data-table__pager {
            margin-bottom: 1rem;
        }

        /* Busy table styling */
        table.b-table[aria-busy='true'] {
            opacity: 0.6;
        }
    }
</style>
